/**
 * REPORTADOR DE ERRORES — ahora respaldado por Firebase Crashlytics.
 *
 * POR QUÉ EXISTE ESTE ARCHIVO TAL COMO ESTÁ:
 * Hasta hoy la app no mandaba NADA a ningún sitio: todo se quedaba en
 * `console.error`, que en un APK de release no lo lee nadie. Por eso llevamos
 * meses diagnosticando por deducción y en la consola de Firebase no hay ni un
 * solo informe de fallo. Crashlytics cierra ese hueco y, lo más importante para
 * lo que estamos persiguiendo, **registra los ANR de Android** (hilo principal
 * bloqueado), que es justo el síntoma por el que Google Play nos rechazó.
 *
 * LO QUE CRASHLYTICS SÍ Y NO VE — para no crearse falsas esperanzas:
 *   · SÍ: errores de JavaScript (incluidas promesas rechazadas sin `catch`),
 *     cierres nativos y ANRs.
 *   · NO: una muerte por falta de memoria nativa (el `lowmemorykiller` de
 *     Android mata el proceso sin avisar a nadie). Eso solo se ve en los
 *     Android Vitals de Play Console.
 *
 * USO:
 *   import { errorReporter } from '../services/errorReporter';
 *   try { ... } catch (e) {
 *     errorReporter.captureException(e, { tag: 'createFoodOrder', uid });
 *   }
 *
 * Un informe sin contexto no sirve para nada: dice "algo falló" y punto. Por eso
 * `setUser()` manda el rol y `setPantalla()` manda la ruta — con eso, en la
 * consola se lee "chofer, en /(tabs)/map" en vez de un identificador anónimo.
 */

import { Platform } from 'react-native';

// `import type` no genera ningún `require` en el bundle: solo trae los tipos.
// El módulo real se carga abajo con `require` perezoso, porque
// @react-native-firebase necesita módulos nativos y en la build WEB (que este
// proyecto sí compila: `npm run deploy:web`) reventaría al importarse.
type ModuloCrashlytics = typeof import('@react-native-firebase/crashlytics');
type InstanciaCrashlytics = ReturnType<ModuloCrashlytics['getCrashlytics']>;

interface ErrorContext {
  tag?: string;
  uid?: string;
  extra?: Record<string, any>;
  severity?: 'fatal' | 'error' | 'warning' | 'info';
}

let modulo: ModuloCrashlytics | null = null;
let instancia: InstanciaCrashlytics | null = null;
let intentado = false;

/**
 * Devuelve la instancia de Crashlytics, o `null` si aquí no la hay (web, o una
 * compilación sin el módulo nativo). Todo lo demás en este archivo tiene que
 * seguir funcionando cuando esto devuelve `null`: un reportador de errores que
 * tumba la app es peor que no tener reportador.
 */
function crashlytics(): InstanciaCrashlytics | null {
  if (instancia) return instancia;
  if (intentado) return null;
  intentado = true;

  if (Platform.OS === 'web') return null;

  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    modulo = require('@react-native-firebase/crashlytics') as ModuloCrashlytics;
    // Instanciar el módulo es lo que engancha el manejador global de JS y el
    // rastreador de promesas rechazadas de la propia librería. No es un
    // "getter" inocente: es el momento en que Crashlytics empieza a escuchar.
    instancia = modulo.getCrashlytics();
  } catch (e) {
    console.warn('[errorReporter] Crashlytics no disponible:', (e as any)?.message);
    modulo = null;
    instancia = null;
  }
  return instancia;
}

/**
 * Enciende Crashlytics al arrancar la app. Llamarlo lo antes posible: los
 * errores que ocurran ANTES de esta llamada no se registran, y el arranque es
 * precisamente donde más se cae la app.
 *
 * Es idempotente y no lanza nunca.
 */
export function instalarCrashlytics(): boolean {
  const c = crashlytics();
  if (!c) return false;
  try {
    // Deja una miga en el informe con la que distinguir un fallo del arranque
    // de uno de más tarde.
    modulo!.log(c, 'app: arranque');
  } catch {
    // Da igual: lo que importaba era instanciar el módulo.
  }
  return true;
}

class ErrorReporter {
  private buffer: Array<{ msg: string; ctx: ErrorContext; ts: number }> = [];
  private MAX_BUFFER = 50;

  /**
   * Reporta una excepción a Crashlytics y la deja en el buffer local.
   */
  captureException(error: Error | unknown, ctx: ErrorContext = {}): void {
    const msg = error instanceof Error ? error.message : String(error);
    const stack = error instanceof Error ? error.stack : undefined;

    console.error(`[errorReporter] ${ctx.tag ?? 'unknown'}: ${msg}`, {
      uid: ctx.uid,
      extra: ctx.extra,
      stack,
    });

    this.bufferPush(msg, ctx);

    const c = crashlytics();
    if (!c || !modulo) return;
    try {
      // El `extra` va como texto en el registro y no como atributo: los
      // atributos de Crashlytics tienen un tope de 64 por informe y se gastan
      // rápido; el registro no.
      if (ctx.extra) {
        modulo.log(c, `${ctx.tag ?? 'sin-tag'}: ${textoCorto(ctx.extra)}`);
      }
      if (ctx.uid) modulo.setAttribute(c, 'uid', ctx.uid).catch(() => {});

      // `jsErrorName` es lo que Crashlytics usa para AGRUPAR. Sin él, todos los
      // errores del proyecto caen en el mismo montón y la consola es inútil;
      // con el tag, cada punto de fallo tiene su propia entrada.
      const err = error instanceof Error ? error : new Error(msg);
      modulo.recordError(c, err, ctx.tag);
    } catch (e) {
      console.warn('[errorReporter] no se pudo reportar:', (e as any)?.message);
    }
  }

  /**
   * Mensaje informativo (no excepción). En Crashlytics queda como "miga de
   * pan": no genera un informe, pero aparece en el que venga después. Es lo que
   * permite reconstruir qué estaba haciendo el usuario justo antes del fallo.
   */
  captureMessage(message: string, ctx: ErrorContext = {}): void {
    console.log(`[errorReporter:msg] ${ctx.tag ?? 'unknown'}: ${message}`, ctx.extra);
    this.bufferPush(message, { ...ctx, severity: ctx.severity ?? 'info' });

    const c = crashlytics();
    if (!c || !modulo) return;
    try {
      modulo.log(c, `${ctx.tag ?? 'msg'}: ${message}`);
    } catch {
      // Una miga perdida no justifica romper nada.
    }
  }

  /**
   * Identifica al usuario actual. El ROL es el dato más valioso del informe:
   * "se cae al aceptar" significa cosas distintas para un chofer, un pasajero,
   * un restaurante o un mensajero, y sin esto no hay forma de saber cuál era.
   */
  setUser(uid: string | null, extras?: { email?: string; role?: string }): void {
    const c = crashlytics();
    if (!c || !modulo) return;
    try {
      // Cadena vacía = sesión cerrada; Crashlytics no acepta null aquí.
      modulo.setUserId(c, uid ?? '').catch(() => {});
      // El correo NO se manda: es dato personal y no aporta nada que no aporte
      // ya el uid, que sí sirve para cruzar con Firestore.
      if (extras?.role) modulo.setAttribute(c, 'rol', extras.role).catch(() => {});
    } catch {
      // ignorado a propósito
    }
  }

  /**
   * Deja constancia de en qué pantalla está el usuario. Un informe que dice
   * "murió en /(tabs)/map" se arregla; uno que solo dice "murió" no.
   */
  setPantalla(ruta: string): void {
    const c = crashlytics();
    if (!c || !modulo || !ruta) return;
    try {
      modulo.setAttribute(c, 'pantalla', ruta.slice(0, 100)).catch(() => {});
      modulo.log(c, `pantalla: ${ruta}`);
    } catch {
      // ignorado a propósito
    }
  }

  /**
   * Devuelve los últimos N errores del buffer local — útil para mostrar
   * en /admin-panel diagnostics. No expone al usuario común.
   */
  getRecentErrors(): typeof this.buffer {
    return [...this.buffer];
  }

  private bufferPush(msg: string, ctx: ErrorContext): void {
    this.buffer.push({ msg, ctx, ts: Date.now() });
    if (this.buffer.length > this.MAX_BUFFER) {
      this.buffer.shift();
    }
  }
}

/**
 * Un registro de Crashlytics tiene tope de tamaño y el objeto `extra` puede
 * traer cualquier cosa (incluida una referencia circular, que rompería
 * JSON.stringify). Se recorta y se blinda aquí, una sola vez.
 */
function textoCorto(valor: unknown): string {
  try {
    return JSON.stringify(valor).slice(0, 400);
  } catch {
    return String(valor).slice(0, 400);
  }
}

export const errorReporter = new ErrorReporter();
